import React from 'react';
import { useTranslation } from 'react-i18next';
import { previewAutomationConditionMatch } from '../services/automationConditionService';
import {
  AUTOMATION_CONDITION_LIMITS,
  AutomationAiCondition,
  AutomationCondition,
  AutomationConditionLogic,
  AutomationConditionRowResult,
  AutomationKeywordCondition,
  AutomationKeywordMatchMode,
  canAddAiCondition,
  canAddCondition,
  emptyAiCondition,
  emptyKeywordCondition,
  evaluateKeywordConditionLocally,
  hasValidAutomationConditions,
  toAutomationConditionRequest,
} from '../types/AutomationCondition';
import styles from './AutomationConditionBuilder.module.css';

type AutomationConditionBuilderProps = {
  conditionLogic: AutomationConditionLogic;
  conditions: AutomationCondition[];
  disabled?: boolean;
  onLogicChange: (logic: AutomationConditionLogic) => void;
  onConditionsChange: (conditions: AutomationCondition[]) => void;
};

export const AutomationConditionBuilder: React.FC<AutomationConditionBuilderProps> = ({
  conditionLogic,
  conditions,
  disabled = false,
  onLogicChange,
  onConditionsChange,
}) => {
  const { t } = useTranslation(['automations']);
  const [draftKeywords, setDraftKeywords] = React.useState<Record<string, string>>({});
  const [sampleText, setSampleText] = React.useState('');
  const [previewing, setPreviewing] = React.useState(false);
  const [previewError, setPreviewError] = React.useState<string | null>(null);
  const [previewMatched, setPreviewMatched] = React.useState<boolean | null>(null);
  const [previewResults, setPreviewResults] = React.useState<AutomationConditionRowResult[]>([]);

  const updateCondition = (id: string, patch: Partial<AutomationCondition>) => {
    onConditionsChange(conditions.map((condition) => (
      condition.id === id ? { ...condition, ...patch } as AutomationCondition : condition
    )));
  };

  const removeCondition = (id: string) => {
    if (conditions.length <= 1) {
      return;
    }
    onConditionsChange(conditions.filter((condition) => condition.id !== id));
  };

  const addKeywordRow = () => {
    if (!canAddCondition(conditions)) {
      return;
    }
    onConditionsChange([...conditions, emptyKeywordCondition('contains', [])]);
  };

  const addAiRow = () => {
    if (!canAddAiCondition(conditions)) {
      return;
    }
    onConditionsChange([...conditions, emptyAiCondition()]);
  };

  const commitKeywordDraft = (condition: AutomationKeywordCondition) => {
    const next = (draftKeywords[condition.id] ?? '').trim();
    if (!next) {
      return;
    }
    const additions = next.split(/[\n,]/).map((item) => item.trim()).filter(Boolean);
    if (additions.length === 0) {
      return;
    }
    updateCondition(condition.id, {
      keywords: [...condition.keywords, ...additions.filter((item) => (
        !condition.keywords.some((existing) => existing.toLowerCase() === item.toLowerCase())
      ))],
    });
    setDraftKeywords((current) => ({ ...current, [condition.id]: '' }));
  };

  const removeKeyword = (condition: AutomationKeywordCondition, keyword: string) => {
    updateCondition(condition.id, {
      keywords: condition.keywords.filter((item) => item !== keyword),
    });
  };

  const handlePreview = async () => {
    if (!hasValidAutomationConditions(conditions)) {
      setPreviewError(t('conditionsRequired'));
      return;
    }

    setPreviewing(true);
    setPreviewError(null);
    try {
      const hasAi = conditions.some((condition) => condition.type === 'ai');
      if (!hasAi) {
        const results = conditions
          .filter((condition): condition is AutomationKeywordCondition => condition.type === 'keyword')
          .map((condition) => ({
            id: condition.id,
            matched: evaluateKeywordConditionLocally(sampleText, condition),
          }));
        const matched = conditionLogic === 'any'
          ? results.some((result) => result.matched)
          : results.every((result) => result.matched);
        setPreviewMatched(matched);
        setPreviewResults(results);
        return;
      }

      const response = await previewAutomationConditionMatch({
        text: sampleText,
        conditionLogic,
        conditions: conditions.map(toAutomationConditionRequest),
      });
      setPreviewMatched(response.matched);
      setPreviewResults(response.results);
    } catch (error) {
      setPreviewMatched(null);
      setPreviewResults([]);
      setPreviewError(error instanceof Error ? error.message : t('previewFailed'));
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div className={styles.builder}>
      <div className={styles.logicRow}>
        <div className={styles.logicTabs} role="group" aria-label={t('conditionLogic')}>
          <button
            type="button"
            className={`${styles.logicTab} ${conditionLogic === 'any' ? styles.logicTabActive : ''}`}
            disabled={disabled}
            onClick={() => onLogicChange('any')}
          >
            {t('matchAny')}
          </button>
          <button
            type="button"
            className={`${styles.logicTab} ${conditionLogic === 'all' ? styles.logicTabActive : ''}`}
            disabled={disabled}
            onClick={() => onLogicChange('all')}
          >
            {t('matchAll')}
          </button>
        </div>
        <p className={styles.hint}>{conditionLogic === 'any' ? t('matchAnyHint') : t('matchAllHint')}</p>
      </div>

      <div className={styles.rows}>
        {conditions.map((condition, index) => (
          <React.Fragment key={condition.id}>
            {index > 0 ? (
              <span className={styles.joiner}>{conditionLogic === 'any' ? t('or') : t('and')}</span>
            ) : null}
            <div className={styles.row}>
              <div className={styles.rowHead}>
                <div className={styles.rowType}>
                  <span className={`${styles.rowBadge} ${condition.type === 'ai' ? styles.rowBadgeAi : ''}`}>
                    {condition.type === 'ai' ? t('aiCheck') : t('keywordCheck')}
                  </span>
                  {t('conditionLabel', { index: index + 1 })}
                </div>
                {conditions.length > 1 ? (
                  <button
                    type="button"
                    className={`btn btn-secondary btn-sm ${styles.rowRemove}`}
                    disabled={disabled}
                    onClick={() => removeCondition(condition.id)}
                  >
                    {t('removeCondition')}
                  </button>
                ) : null}
              </div>

              {condition.type === 'keyword' ? (
                <div className={styles.rowFields}>
                  <label className={styles.field}>
                    {t('keywordMatchMode')}
                    <select
                      className="text-input"
                      value={condition.matchMode}
                      disabled={disabled}
                      onChange={(event) => updateCondition(condition.id, {
                        matchMode: event.target.value as AutomationKeywordMatchMode,
                      })}
                    >
                      <option value="contains">{t('matchContains')}</option>
                      <option value="equals">{t('matchEquals')}</option>
                      <option value="starts_with">{t('matchStartsWith')}</option>
                    </select>
                  </label>
                  <label className={styles.field}>
                    {t('keywords')}
                    <div className={styles.chips}>
                      {condition.keywords.map((keyword) => (
                        <span key={`${condition.id}-${keyword}`} className={styles.chip}>
                          {keyword}
                          <button
                            type="button"
                            className={styles.chipRemove}
                            disabled={disabled}
                            aria-label={t('removeKeyword', { keyword })}
                            onClick={() => removeKeyword(condition, keyword)}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      <input
                        className={styles.chipInput}
                        value={draftKeywords[condition.id] ?? ''}
                        disabled={disabled}
                        placeholder={t('keywordsPlaceholder')}
                        onChange={(event) => setDraftKeywords((current) => ({
                          ...current,
                          [condition.id]: event.target.value,
                        }))}
                        onBlur={() => commitKeywordDraft(condition)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ',') {
                            event.preventDefault();
                            commitKeywordDraft(condition);
                          }
                          if (event.key === 'Backspace' && !(draftKeywords[condition.id] ?? '') && condition.keywords.length > 0) {
                            removeKeyword(condition, condition.keywords[condition.keywords.length - 1]);
                          }
                        }}
                      />
                    </div>
                    <p className={styles.hint}>{t('keywordsHint')}</p>
                  </label>
                </div>
              ) : (
                <div className={styles.aiFields}>
                  <label className={styles.field}>
                    {t('aiQuestion')}
                    <input
                      className="text-input"
                      value={(condition as AutomationAiCondition).question}
                      disabled={disabled}
                      maxLength={AUTOMATION_CONDITION_LIMITS.questionMaxLength}
                      placeholder={t('aiQuestionPlaceholder')}
                      onChange={(event) => updateCondition(condition.id, { question: event.target.value })}
                    />
                    <p className={styles.hint}>{t('aiQuestionHint')}</p>
                  </label>
                  <label className={styles.field}>
                    {t('expectedAnswer')}
                    <select
                      className="text-input"
                      value={(condition as AutomationAiCondition).expectedAnswer}
                      disabled={disabled}
                      onChange={(event) => updateCondition(condition.id, {
                        expectedAnswer: event.target.value === 'no' ? 'no' : 'yes',
                      })}
                    >
                      <option value="yes">{t('yes')}</option>
                      <option value="no">{t('no')}</option>
                    </select>
                  </label>
                  <label className={styles.field}>
                    {t('minConfidence')}
                    <span className={styles.confidenceValue}>
                      {(condition as AutomationAiCondition).minConfidence}/10
                    </span>
                    <input
                      type="range"
                      min={AUTOMATION_CONDITION_LIMITS.minConfidence}
                      max={AUTOMATION_CONDITION_LIMITS.maxConfidence}
                      value={(condition as AutomationAiCondition).minConfidence}
                      disabled={disabled}
                      onChange={(event) => updateCondition(condition.id, {
                        minConfidence: Number(event.target.value),
                      })}
                    />
                    <p className={styles.hint}>{t('minConfidenceHint')}</p>
                  </label>
                </div>
              )}
            </div>
          </React.Fragment>
        ))}
      </div>

      <div className={styles.addRow}>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={disabled || !canAddCondition(conditions)}
          onClick={addKeywordRow}
        >
          {t('addKeywordCondition')}
        </button>
        <button
          type="button"
          className={`btn btn-secondary btn-sm ${styles.addAi}`}
          disabled={disabled || !canAddAiCondition(conditions)}
          onClick={addAiRow}
        >
          {t('addAiCondition')}
        </button>
      </div>

      <div className={styles.tryBox}>
        <h5 className={styles.tryTitle}>{t('tryTitle')}</h5>
        <p className={styles.hint}>{t('tryHint')}</p>
        <div className={styles.tryRow}>
          <input
            className={`text-input ${styles.tryInput}`}
            value={sampleText}
            disabled={disabled || previewing}
            placeholder={t('tryPlaceholder')}
            onChange={(event) => setSampleText(event.target.value)}
          />
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={disabled || previewing}
            onClick={() => void handlePreview()}
          >
            {previewing ? t('trying') : t('tryButton')}
          </button>
        </div>
        {previewError ? <p className={styles.hint}>{previewError}</p> : null}
        {previewMatched !== null ? (
          <div className={styles.tryResults}>
            <span className={`${styles.tryOverall} ${previewMatched ? styles.tryPass : styles.tryFail}`}>
              {previewMatched ? t('tryWouldMatch') : t('tryWouldNotMatch')}
            </span>
            {conditions.map((condition) => {
              const result = previewResults.find((item) => item.id === condition.id);
              const label = condition.type === 'ai'
                ? condition.question || t('aiCheck')
                : condition.keywords.join(', ') || t('keywordCheck');
              const detail = result?.confidence
                ? t('tryAiDetail', { answer: result.answer ?? '—', confidence: result.confidence })
                : null;
              return (
                <div key={`preview-${condition.id}`} className={styles.tryResult}>
                  <span>{label}</span>
                  <span className={result?.matched ? styles.tryPass : styles.tryFail}>
                    {result?.matched ? t('tryPass') : t('tryFail')}
                    {detail ? ` · ${detail}` : ''}
                  </span>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
};
